import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";
import { NewShipmentDateOffset } from "../../data/org-settings/types";
import { CargoTemplateFromApi } from "./index";
import * as t from "io-ts";
import { Stop, stopT } from "../cargo-and-template-shared";
import { apiStopToFormStop } from "../cargo-dialog/helpers";
import { CargoFormData } from "../cargo-dialog/types";
import { convertStopUnitsStringToDecimal } from "dora-shared";

export const getStopDate = (daysAdjustment: NewShipmentDateOffset | null) =>
  DateTime.now()
    .plus({ day: parseInt(daysAdjustment || "1") })
    .toFormat("yyyy-MM-dd");

const defaultLoadmeters = 13.6;

export const emptyStop = (
  newShipmentDateOffset: NewShipmentDateOffset | null
): Stop => ({
  provisoryId: uuid(), // Generate temporary ID for stopUnit references
  flipKey: uuid(),
  loadmeters: 0,
  modified: false,
  fix: false,
  place: null,
  weight: "",
  colli: "",
  length: "",
  height: "",
  width: "",
  date: getStopDate(newShipmentDateOffset),
  cubicMeters: "",
  temperature: "",
  time: "",
  note: null,
  ref: "",
  tag: "",
  contact: "",
  contactPhone: "",
  pieces: [],
  openingTime: "",
  openingDate: "",
});

export const defaultFormData = ({
  assignedTeamId,
  newShipmentDateAdjustment,
}: {
  assignedTeamId: string;
  newShipmentDateAdjustment: NewShipmentDateOffset | null;
}): CargoFormData => {
  return {
    clientContact: "",
    clientContactEmail: "",
    clientContactPhone: "",
    driverCanAddDropoffs: false,
    driverCanRegisterTime: false,
    driverCanMoveFromToUndispatched: false,
    invoiceRef: "",
    cargoTagId: null,
    smartMatch: false,
    customerContactId: null,
    description: "",
    description2: "",
    qualities: [],
    requirements: [],
    products: [],
    type: "MISCELLANEOUS",
    note: "",
    assignedTeamId,
    minTemperature: "",
    maxTemperature: "",
    containerNo: "",
    adr: false,
    adrText: "",
    hideForDrivers: false,
    invoiceStatus: "OPEN",
    pickupList: [
      {
        ...emptyStop(newShipmentDateAdjustment),
        loadmeters: defaultLoadmeters,
        modified: false,
      },
    ],
    dropoffList: [
      {
        ...emptyStop(newShipmentDateAdjustment),
        loadmeters: defaultLoadmeters,
        modified: false,
      },
    ],
    stopUnits: [],
    cmrConfigs: [],
    extraCo2Elements: [],
  };
};

type ApiStop = t.TypeOf<typeof stopT>;

export const apiCargoTemplateToFormData = (
  data: CargoTemplateFromApi
): CargoFormData => ({
  ...data,
  // Cargo templates don't carry CMR config.
  cmrConfigs: [],
  clientId: data.clientId || undefined,
  customerContactId: data.customerContactId || null,
  invoiceRef: data.invoiceRef || null,
  description: data.description || "",
  description2: data.description2 || "",
  qualities: data.qualities || [],
  requirements: data.requirements || [],
  products: data.products,
  clientContactEmail: data.clientContactEmail || "",
  clientContactPhone: data.clientContactPhone || "",
  clientContact: data.clientContact || "",
  driverCanAddDropoffs: data.driverCanAddDropoffs || false,
  minTemperature: data.minTemperature || "",
  maxTemperature: data.maxTemperature || "",
  containerNo: data.containerNo || "",
  pickupList: data.pickupList.map(apiStopToFormStop),
  dropoffList: data.dropoffList.map(apiStopToFormStop),
  stopUnits:
    data.stopUnits.map((su) => ({
      ...su,
      ...convertStopUnitsStringToDecimal(su),
    })) || [],
  note: data.note || "",
});

export const formDataStopToApiStop = (stop: Stop): ApiStop => {
  return {
    ...stop,
    loadmeters: stop.loadmeters.toString(),
  };
};
